import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getMedicalCertificateByAppointmentId_apiCalls } from "../../api/api_calls/appointmentforms/medicalCertificate_apiCalls";
import DocMobilLogo from "../../assets/svgs/DocMobilLogoWithText.svg";
import DownloadMedicalCertificateForm from "./downloadforms/DownloadMedicalCertificateForm";

function getAgeFromDOB(dob) {
  if (!dob) return "N/A";
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

const MedicalCertificateFilled = ({ showDownloadButton }) => {
  const [loading, setLoading] = useState(true);
  const [certificateData, setCertificateData] = useState(null);
  const location = useLocation();

  // Get appointmentId from props or location state as fallback
  const finalAppointmentId =
    location.state?.appointmentId || location.state?.appointment?.id;

  // Fetch medical certificate data
  useEffect(() => {
    const fetchMedicalCertificate = async () => {
      if (!finalAppointmentId) {
        console.log("No appointment ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log(
          "Fetching medical certificate for appointmentId:",
          finalAppointmentId,
        );

        const response = await getMedicalCertificateByAppointmentId_apiCalls({
          appointmentId: finalAppointmentId,
        });

        console.log("Medical certificate API response:", response);

        if (response?.data) {
          setCertificateData(response.data);
          console.log("Medical certificate data loaded:", response.data);
        } else {
          console.log("No medical certificate found for this appointment");
        }
      } catch (error) {
        console.error("Failed to fetch medical certificate:", error);
        // Don't set error state, just log it
      } finally {
        setLoading(false);
      }
    };

    fetchMedicalCertificate();
  }, [finalAppointmentId]);

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>Loading medical certificate...</span>
      </div>
    );
  }
  console.log("certificateData :", certificateData);
  // Use certificateData or empty placeholders to prevent crashes
  const displayData = certificateData || {
    id: null,
    appointmentId: finalAppointmentId,
    diagnosis: null,
    recommendations: null,
    createdAt: null,
    updatedAt: null,
    appointment: {
      doctor: {
        firstName: null,
        lastName: null,
        medicalLicenseNumber: null,
        contactNumber: null,
        address: null,
        signature: {
          url: null,
        },
        qualifications: [],
      },
      patient: {
        id: null,
        firstName: null,
        lastName: null,
        medicalLicenseNumber: null,
        contactNumber: null,
        address: null,
        signature: {
          url: null,
        },
        dob: null,
      },
    },
  };

  return (
    <div className="rounded-lg shadow-sm w-full mx-auto bg-white relative">
      <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
        <img src={DocMobilLogo || "/placeholder.svg"} alt="" className="w-96" />
      </div>

      <div className="relative z-10">
        {showDownloadButton && (
          <div className="flex flex-row items-center justify-between mb-2">
            <h1 className="text-2xl font-bold">Medical Certificate</h1>
            <DownloadMedicalCertificateForm
              displayData={displayData}
              appointmentId={finalAppointmentId}
            />
          </div>
        )}
        <h2 className="text-xl font-bold mb-4">DocMobil</h2>

        <hr className="border-blue-500 mb-6" />

        <div className="space-y-2 mb-6">
          <p className="text-xl font-bold text-[#1E1E1E]">
            <span className="text-[#1E1E1EB2]">Date : </span>
            {displayData?.createdAt
              ? new Date(displayData.createdAt).toLocaleDateString()
              : new Date().toLocaleDateString()}
          </p>
          <p className="text-xl font-bold text-[#1E1E1E]">
            <span className="text-[#1E1E1EB2]">Patient Name : </span>
            {displayData?.appointment?.patient?.firstName || "N/A"}{" "}
            {displayData?.appointment?.patient?.lastName || "N/A"}
          </p>
          <p className="text-xl font-bold text-[#1E1E1E]">
            <span className="text-[#1E1E1EB2]">Age: </span>
            {getAgeFromDOB(displayData?.appointment?.patient?.dob)}
          </p>
          <p className="text-xl font-bold text-[#1E1E1E]">
            <span className="text-[#1E1E1EB2]">Patinet ID : </span>
            {displayData?.appointment?.patient?.id || "N/A"}
          </p>
        </div>

        <p className="text-base text-[#1E1E1E] font-semibold mb-6">
          This is to certify that the patient was examined and treated at our
          clinic/hospital on{" "}
          <span className="text-[#1E1E1E] text-xl font-bold">
            {displayData?.createdAt
              ? new Date(displayData.createdAt).toLocaleDateString()
              : new Date().toLocaleDateString()}
          </span>
          . The patient has been diagnosed with the following medical condition:
        </p>

        <div className="mb-6">
          <h3 className="text-xl font-bold text-[#1E1E1E] mb-2">
            Medical Condition/Diagnosis:
          </h3>
          <p className="text-base text-[#1E1E1E] font-semibold">
            {displayData?.diagnosis || "N/A"}
          </p>
        </div>

        <div className="mb-6">
          <h3 className="text-xl font-bold text-[#1E1E1E] mb-2">
            Recommendations:
          </h3>
          <p className="text-base text-[#1E1E1E] font-semibold">
            {displayData?.recommendations || "N/A"}
          </p>
        </div>

        <div className="mt-12">
          <p className="text-xl font-bold text-[#1E1E1E] mb-2">
            Authorized Medical Officer
          </p>

          <p className="text-xl font-bold text-[#1E1E1E] mb-2">
            {displayData?.appointment?.doctor?.firstName || "N/A"}{" "}
            {displayData?.appointment?.doctor?.lastName || "N/A"}
          </p>
          <p className="text-xl font-bold text-[#1E1E1E] mb-2">
            {displayData?.appointment?.doctor?.qualifications?.join(", ")}
          </p>
          <div className="font-script text-lg">
            <img
              alt="Medical Officer Signature"
              style={{ width: "150px", height: "50px" }}
              src={displayData?.appointment?.doctor?.signature?.url}
            />
          </div>

          <p className="text-sm text-gray-500 mt-2">
            Certificate ID: {displayData?.id || "N/A"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MedicalCertificateFilled;
